import { USER_LOAN_PROGRAM } from '@/config/constants';
import {
  CalculateClosingDisclosureProps,
  calculateTaxFactor,
  calculateTransferTaxFactor,
} from '.';

/**
 * Cash purchase estimate: 0 interest, no loan, no lender fees.
 * Cash to close = purchase price + buyer-side title/gov/prepaid costs − credits.
 */
export const calculateCashClosingDisclosure = ({
  offerPrice,
  listPrice,
  downPayment: _downPayment,
  zipCode = 0,
  hoaFees = 0,
  commission = 0,
  monthlyPropertyTaxes: monthlyPropertyTaxesOverride,
  monthlyHomeownersInsurance: monthlyHomeownersInsuranceOverride,
  marketInterestRate = 0,
  creditScore = 0,
  loanTerm = 0,
}: CalculateClosingDisclosureProps) => {
  const purchasePrice = offerPrice || listPrice;
  const downPayment = purchasePrice;
  const loanAmount = 0;
  const interestRate = 0;
  const ltv = 0;

  const taxFactor = calculateTaxFactor(zipCode);
  const transferTaxFactor = calculateTransferTaxFactor(zipCode);

  const titleTitleSearchBase = 250;
  const recordingDeedBase = 75;
  const optionalOwnersTitleInsurance = purchasePrice * 0.0035;
  const titleSettlementAgentFee = 650;
  const insuranceBinder = 25;
  const surveyFee = 450;
  const pestInspectionFee = 0;
  const earnestMoneyDeposit = 0;
  const lenderCredits = 0;

  const annualPropertyTax = (purchasePrice * 0.0125 * taxFactor) / 12;
  const monthlyPropertyTaxesBase = annualPropertyTax;
  const monthlyPropertyTaxes =
    monthlyPropertyTaxesOverride ?? monthlyPropertyTaxesBase;
  const monthlyHomeownersInsuranceBase = (purchasePrice * 0.01035) / 12;
  const monthlyHomeownersInsurance =
    monthlyHomeownersInsuranceOverride ?? monthlyHomeownersInsuranceBase;

  const upfrontHomeownersInsurance = purchasePrice * 0.01035;
  const prepaidsHomeownersInsurance = monthlyHomeownersInsurance * 3;
  const prepaidsPropertyTaxes = monthlyPropertyTaxes * 3;
  const titleOwnersTitleInsuranceBase = purchasePrice * 0.0045;
  const homeInspectionFeeBase = purchasePrice * 0.0035;
  const homeInspectionFee = Math.min(
    Math.max(homeInspectionFeeBase, 650),
    4500
  );

  const transferTaxes = purchasePrice * transferTaxFactor;
  const titleCosts =
    optionalOwnersTitleInsurance +
    titleTitleSearchBase +
    titleOwnersTitleInsuranceBase +
    surveyFee +
    titleSettlementAgentFee +
    insuranceBinder;
  const govCosts = recordingDeedBase + transferTaxes;
  const prepaidCosts =
    prepaidsPropertyTaxes +
    prepaidsHomeownersInsurance +
    upfrontHomeownersInsurance;
  const lenderCostTotals = 0;
  const closingCosts = titleCosts + govCosts + prepaidCosts;
  const cashToClose = closingCosts + downPayment + earnestMoneyDeposit;
  const cashOutsideClosing = earnestMoneyDeposit + homeInspectionFee;
  const unbeatableCredit = (purchasePrice * commission) / 100;
  const unbeatableDeal = cashToClose - unbeatableCredit;

  return {
    type: USER_LOAN_PROGRAM.CASH,
    offerPrice,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate: 0,
    creditScore,
    downPayment,
    loanAmount,
    interestRate,
    ltv,
    upfrontHomeownersInsurance,
    cleanPayment: monthlyPropertyTaxes + monthlyHomeownersInsurance + hoaFees,
    cashOutsideClosing,
    monthlyPMIPayment: 0,
    processingFeeBase: 0,
    principalAndInterest: 0,
    homeInspectionFee,
    loanTerm,
    optionalOwnersTitleInsurance,
    hoaFees,
    earnestMoneyDeposit,
    piti: monthlyPropertyTaxes + monthlyHomeownersInsurance + hoaFees,
    estimatedEscrow: monthlyPropertyTaxes + monthlyHomeownersInsurance,
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    prepaidsHomeownersInsurance,
    prepaidsPropertyTaxes,
    prepaidInterest: 0,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase: 0,
    titleTitleSearchBase,
    insuranceBinder,
    recordingDeedBase,
    appraisalFeeBase: 0,
    underwritingFee: 0,
    applicationFeeBase: 0,
    pestInspectionFee,
    floodMonitoringFee: 0,
    floodDeterminationFee: 0,
    creditReportFeeBase: 0,
    recordingTitle: 0,
    recordingMortgage: 0,
    countyRecordingFees: 0,
    stateRecordingFees: 0,
    surveyFee,
    initialEscrowTaxes: 0,
    initialEscrowMortgageInsurance: 0,
    initialEscrowHOI: 0,
    lenderCredits,
    lenderCostTotals,
    titleCosts,
    govCosts,
    prepaidCosts,
    closingCosts,
    cashToClose,
    unbeatableDeal,
    transferTaxes,
    removePrepaymentPenalty: false,
    prepaymentPenaltyYear1: 0,
    prepaymentPenaltyYear2: 0,
    prepaymentPenaltyYear3: 0,
    prepaymentPenaltyYear4: 0,
    prepaymentPenaltyYear5: 0,
    additionalLenderOriginationFee: 0,
  };
};

export type ProductCashEsitmation = ReturnType<
  typeof calculateCashClosingDisclosure
>;
